import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import axiosInstance from "../../../utils/config/config";
import { businessPracticeData } from "../../../assets/data/data";
import {
  API_ENDPOINT,
  CHECKLIST_SKIP_KEYS,
  ERROR_MESSAGE,
} from "./Business.constants";

const buildInitialCheckedItems = () =>
  businessPracticeData.reduce((acc, item) => {
    acc[item.value] = { id: item.id, checked: false };
    return acc;
  }, {});

const formatChecklistForApi = (checkedItems) =>
  Object.entries(checkedItems).map(([key, value]) => ({
    value: key,
    id: value.id,
    checked: value.checked,
  }));

const parseChecklistResponse = (data) => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (CHECKLIST_SKIP_KEYS.includes(key)) return acc;
    acc[key] = { id: data.id, checked: value };
    return acc;
  }, {});
};

export const useBusinessChecklist = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });
  const [checkedItems, setCheckedItems] = useState(buildInitialCheckedItems);

  const handleCheck = useCallback((value) => {
    setCheckedItems((prev) => ({
      ...prev,
      [value]: { ...prev[value], checked: !prev[value].checked },
    }));
  }, []);

  const saveChecklist = useCallback(async (updatedCheckedItems) => {
    const checklistData = formatChecklistForApi(updatedCheckedItems);

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        API_ENDPOINT,
        checklistData
      );

      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        setErrorState({ error: false, message: "" });
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      setErrorState({ error: true, message: ERROR_MESSAGE });
    }
  }, []);

  const debouncedSave = useMemo(
    () => debounce(saveChecklist, 1000),
    [saveChecklist]
  );

  const fetchChecklist = useCallback(async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINT);

      if (response?.data?.businessPracticeChecklist) {
        const data = response.data.businessPracticeChecklist;
        const updatedChecklist = parseChecklistResponse(data);
        setCheckedItems(updatedChecklist);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    debouncedSave(checkedItems);
    return () => debouncedSave.cancel();
  }, [checkedItems, debouncedSave]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const clearError = useCallback(() => {
    setErrorState({ error: false, message: "" });
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccess(false);
  }, []);

  return {
    loading,
    success,
    errorState,
    checkedItems,
    handleCheck,
    clearError,
    clearSuccess,
  };
};
